import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, forkJoin, Observable} from 'rxjs';
import {catchError, finalize, takeUntil, tap} from 'rxjs/operators';

import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import sortBy from 'lodash-es/sortBy';

import {Destroyable} from '../../../destroyable.component';
import {PaySerie} from '../../../models/models.interfaces';
import {AccountingUtilsService} from '../../../shared/accounting/accounting-utils.service';
import {AccountingPlan, AccountingPlanPaySerieDTO} from '../../../shared/accounting/accounting.model';
import {AccountingService} from '../../../shared/accounting/accounting.service';
import {RolesService} from '../../../shared/authentication/roles/roles.service';
import {ChartService} from '../../../shared/charts/chart.service';
import {ModalService} from '../../../shared/modal';
import {DefaultPagination, Pagination} from '../../../shared/ui';

export interface AccountingPlanView {
  id: number;
  name: string;
  paySeries: PaySerie[];
  payArrayNames?: string[];
  newPlan: boolean;
  editMode: boolean;
}

export const isAccountingPlanEqual = (modifiedAccountingPlan: AccountingPlanView, originalAccountingPlan: AccountingPlanView): boolean => {
  const paySerieIdFromAccountingPlanView = modifiedAccountingPlan.paySeries.map(apv => apv.id);
  const paySerieIdFromAccountingPlan = originalAccountingPlan.paySeries.map(ap => ap.id);
  return modifiedAccountingPlan.id === originalAccountingPlan.id && modifiedAccountingPlan.name === originalAccountingPlan.name &&
    isEqual(paySerieIdFromAccountingPlan, paySerieIdFromAccountingPlanView)
    && isStringArrayEqual(originalAccountingPlan.payArrayNames, modifiedAccountingPlan.payArrayNames);
};

export const isStringArrayEqual = (originalValue: string[], modifiedValues: string[]): boolean => {
  const sortedOriginalValue = sortBy(originalValue);
  const sortedModifiedValue = sortBy(modifiedValues);
  return isEqual(sortedOriginalValue, sortedModifiedValue);

};

export const createAccountingPlanPaySerieDto = (ap: AccountingPlanView): AccountingPlanPaySerieDTO => {
  const accountingPaySerieDTO = new AccountingPlanPaySerieDTO();
  const accountingPlan = new AccountingPlan();
  accountingPlan.id = ap.id;
  accountingPlan.name = ap.name;
  accountingPlan.payArrayNames = ap.payArrayNames;
  accountingPaySerieDTO.accountingPlan = accountingPlan;
  accountingPaySerieDTO.paySeriesIds = ap.paySeries.map(ps => parseInt(ps.id, 10));
  return accountingPaySerieDTO;
};

export const createAccountingPlanView = (accountingPlan: AccountingPlan, paySeriesList: PaySerie[]): AccountingPlanView => ({
  id: accountingPlan.id,
  name: accountingPlan.name,
  payArrayNames: accountingPlan.payArrayNames,
  paySeries: paySeriesList,
  newPlan: false,
  editMode: false
});

@Component({
  selector: 'gp-accounting-plan-management',
  templateUrl: './accounting-mapping-management.component.html',
  styleUrls: ['./accounting-mapping-management.component.scss']
})
export class AccountingMappingManagementComponent extends Destroyable implements OnInit, OnDestroy {

  public paySeries: PaySerie[] = [];
  public originalPaySeries: PaySerie[] = [];

  public selectedAccountingPlan: AccountingPlan = null;
  public initialAccountingPlans: AccountingPlanView[] = [];

  public accountingPlanForm: UntypedFormGroup;

  public dataLoaded: EventEmitter<void> = new EventEmitter<void>();

  public saveInProgress: boolean;
  public isAccountingPlanLoading = false;

  public pagination: Pagination = new DefaultPagination();

  public cliRegIds: number[];

  public filteredPayArrayNames;

  public isAccountantAdmin = false;
  public showRules = false;

  @ViewChildren('accountingPlanNameInput')
  public accountingPlanNameInput: QueryList<ElementRef>;
  @ViewChild('tableBody', {static: false})
  public tableBody: ElementRef;

  private removeClickOutsideListener: Function;
  private originalCliRegIds: number[] = [];

  constructor(
    public formBuilder: UntypedFormBuilder,
    private element: ElementRef,
    private renderer: Renderer2,
    private accountingService: AccountingService,
    private chartService: ChartService,
    private router: Router,
    private translate: TranslateService,
    private modalService: ModalService,
    private rolesService: RolesService) {
    super();
  }

  ngOnInit(): void {
    this.filteredPayArrayNames = [...AccountingUtilsService.originalPayArrayNames];
    this.filterDefaultPayArrayNames();
    this.isAccountantAdmin = this.rolesService.hasRole('ACCOUNTANT')
      && this.rolesService.hasRole('CONFIGURATION');
    this.populateAccountingPlanAndPaySeries();

    this.pagination.pageChanged
      .asObservable()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.populateAccountingPlanAndPaySeries());
  }

  get accountingPlan(): UntypedFormArray {
    return <UntypedFormArray>this.accountingPlanForm.get('accountingPlans');
  }

  private filterDefaultPayArrayNames() {
    this.filteredPayArrayNames = [...AccountingUtilsService.originalPayArrayNames].filter(payArray => {
      return !(payArray === 'TitlePayslipFR' || payArray === 'TaxableSalaryFR' || payArray === 'NetPayBeforeTax');
    });
  }

  public editCell(accountingPlanView: AccountingPlanView, planIndex: number): void {

    if (!accountingPlanView.editMode) {
      this.addClickOutsideListener();
      accountingPlanView.editMode = true;
    }
    this.accountingPlan.controls.forEach((ctrl, index) => {
      if (index !== planIndex) {
        const value = ctrl.value;
        value.editMode = false;
        ctrl.patchValue(value);
      }
    });
    this.accountingPlan.at(planIndex).patchValue(accountingPlanView);
  }

  public addClickOutsideListener(): void {
    this.removeClickOutsideListener = this.renderer.listen('document', 'click', (event) => {
      if (event.target && document.contains(event.target) && !this.tableBody.nativeElement.contains(event.target)) {
        this.disabledEditModeAndSaveValidAccountingPlan();
      }
    });
  }

  public addNewAccountingPlan(): void {
    const control = <UntypedFormArray>this.accountingPlanForm.get('accountingPlans');
    control.push(this.initialAccountingPlanForm());
    this.pagination.totalItems += 1;
    this.changeRowToUnEditable();
    this.setFocusToNewRow();

    setTimeout(() => {
      document?.getElementById('name' + (this.pagination.totalItems - 1))?.scrollIntoView();
    });
  }

  public editAccountingPlan(event: MouseEvent, value: AccountingPlanView): void {
    event.stopPropagation();
    this.cliRegIds = [...this.originalCliRegIds];
    this.selectedAccountingPlan = value;
    if (value.paySeries?.length) {
      this.cliRegIds = value.paySeries.flatMap(ps => !ps.cliRegIds ? [] : ps.cliRegIds);
    }
    this.showRules = true;
  }

  public deleteAccountingPlan(event: MouseEvent, index: number, accountingPlan: AccountingPlanView) {
    event.stopPropagation();
    let foundAccountingRules = 0;
    if (!accountingPlan.newPlan) {
      return this.accountingService.getAccountingRules(accountingPlan.id, 9999, 0, '').pipe(tap((response => {
        foundAccountingRules = response.payload.length;
        if (foundAccountingRules > 0) {
          this.modalService.alert({
            title: 'COMMON.alert',
            text: 'ACCOUNTING.ACCOUNTING_PLAN_MAPPING.delete_warning'
          });
        } else {
          this.confirmDeletion(event, index, accountingPlan);
        }

      }))).subscribe();
    } else {
      this.confirmDeletion(event, index, accountingPlan);
    }
  }

  public confirmDeletion(event: MouseEvent, index: number, accountingPlan: AccountingPlanView): void {
    this.modalService.confirm({
      'title': 'ACCOUNTING.accounting_mapping',
      'text': 'ACCOUNTING.ACCOUNTING_MAPPING.DIALOG.block.delete',
      'customClass': '',
      'type': 'danger'
    }).then((confirmed: boolean) => {
      if (confirmed) {
        if (!accountingPlan.newPlan) {
          this.accountingService.deleteAccountingPlan(accountingPlan.id).subscribe(() => {
            this.populateAccountingPlanAndPaySeries();
          });
        } else {
          const control = <UntypedFormArray>this.accountingPlanForm.get('accountingPlans');
          control.removeAt(index);
          this.pagination.totalItems -= 1;
        }

      }
    });
  }

  public createAccountingPlanForm(): void {
    this.accountingPlanForm = this.formBuilder.group({accountingPlans: this.formBuilder.array([])});
  }

  public filterPaySeriesBasedOnInput(event: string) {
    this.paySeries = [...this.originalPaySeries];
    if (!isEmpty(event)) {
      this.paySeries = this.paySeries.filter(ps => ps.transId.toLowerCase().includes(event.toLowerCase()));
    }
  }

  public filterPayArraysBasedOnInput(input: string) {
    this.filteredPayArrayNames = [...AccountingUtilsService.originalPayArrayNames];
    if (!isEmpty(input)) {
      this.filteredPayArrayNames = this.filteredPayArrayNames.filter(arrayName => arrayName.toLowerCase()
        .includes(input.toLowerCase()))
        .sort((a, b) => (a.toLowerCase().indexOf(input.toLowerCase()) < b.toLowerCase().indexOf(input.toLowerCase()) ? -1 : 1));
      return this.filteredPayArrayNames;
    }
    return this.filteredPayArrayNames;
  }

  public canAddBtnBeEnabled() {
    const editMode = this.accountingPlan.controls.find(x => x.value.editMode);
    return this.accountingPlanForm.valid && !editMode;
  }

  public getUnSelectedPaySeries() {
    if (this.paySeries?.length) {
      this.accountingPlan.controls.forEach(accPlanFc => {
        const accPlan = accPlanFc.value;
        accPlan.paySeries.forEach(selectedPaySerie => {
          this.paySeries = this.paySeries.filter(ps => ps.transId !== selectedPaySerie.transId);
        });
      });
      return this.paySeries;
    }
    return [];
  }

  private disabledEditModeAndSaveValidAccountingPlan() {
    this.changeRowToUnEditable();
    if (this.accountingPlanForm.valid && !this.saveInProgress) {
      this.saveAccountingPlan();
    }
  }

  private changeRowToUnEditable() {
    this.accountingPlan.controls.forEach((fg) => {
      const value = fg.value;
      value.editMode = false;
      fg.patchValue(value);
    });
  }

  private initialAccountingPlanForm(): UntypedFormGroup {
    return this.formBuilder.group({
      id: ['', ''],
      name: ['', Validators.required],
      paySeries: [[]],
      payArrayNames: [[]],
      newPlan: [true, ''],
      editMode: [false, '']
    });
  }

  private createFromGroupFromAccountingPlan(accountingPlan: AccountingPlanView): UntypedFormGroup {
    return this.formBuilder.group({
      id: [accountingPlan.id, ''],
      name: [accountingPlan.name, Validators.required],
      paySeries: [accountingPlan.paySeries],
      payArrayNames: [this.getPayArrayNamesForm(accountingPlan)
      ],
      editMode: [accountingPlan.editMode, ''],
      newPlan: [accountingPlan.newPlan, '']
    });
  }

  getPayArrayNamesForm(accountingPlan: AccountingPlanView) {
    if (accountingPlan.payArrayNames?.length) {
      return accountingPlan.payArrayNames;
    }
    if (this.isAccountantAdmin) {
      return this.filteredPayArrayNames;
    }
    return [];
  }

  private populateAccountingPlanAndPaySeries() {
    this.createAccountingPlanForm();
    this.isAccountingPlanLoading = true;
    this.dataLoaded.next(null);
    this.pagination.resetPageIfNeeded({search: 'Search', sort: ''});
    this.cliRegIds = [];
    this.originalCliRegIds = [];
    this.originalPaySeries = [];
    this.initialAccountingPlans = [];
    forkJoin([
      this.chartService.getPaySeries({perPage: 99}),
      this.accountingService.getAccountingPlans(this.pagination.perPage, this.pagination.currentPage.number, null)
    ])
      .pipe(
        tap(([paySeriesResponse, accountingResponse]) => {
          this.paySeries = [];
          this.paySeries = paySeriesResponse.payload;
          this.originalPaySeries = paySeriesResponse.payload;
          this.pagination.update(accountingResponse.total);
          const accountingPlans = [...accountingResponse.payload];
          this.cliRegIds = this.paySeries.flatMap(ps => !ps.cliRegIds ? [] : ps.cliRegIds);
          this.originalCliRegIds = [...this.cliRegIds];
          accountingPlans.forEach(accountingPlan => {
            const paySeriesList = this.paySeries.filter(ps => ps.accountingPlan?.id === accountingPlan.id);
            const acntPlanView = createAccountingPlanView(accountingPlan, paySeriesList);
            this.pushToAccountingPlanList(acntPlanView, paySeriesList);
            this.accountingPlan.push(this.createFromGroupFromAccountingPlan(acntPlanView));
          });
        }),
        finalize(() => {
          this.dataLoaded.next(null);
          this.isAccountingPlanLoading = false;
        }))
      .subscribe();
  }

  private pushToAccountingPlanList(accountingPlan: AccountingPlanView, paySeriesList: PaySerie[]) {
    accountingPlan.paySeries = paySeriesList;
    this.initialAccountingPlans.push(accountingPlan);
  }

  private setFocusToNewRow() {
    setTimeout(() => {
      const accountingPlanFormArray = this.accountingPlan;
      const index = accountingPlanFormArray.length - 1;
      const addedValue = accountingPlanFormArray.at(index).value;
      addedValue.editMode = true;
      accountingPlanFormArray.at(index).patchValue(addedValue);
      this.addClickOutsideListener();
      if (this.accountingPlanNameInput) {
        this.accountingPlanNameInput.last.nativeElement.focus();
      }
    });
  }

  private saveAccountingPlan() {
    const accountingPlanValues = this.accountingPlan.value;
    const actionsToSave$: Observable<string>[] = [];
    accountingPlanValues.forEach(accountingPlanValue => {
      if (!accountingPlanValue.id) {
        const accountingPlanPaySerieDTO = createAccountingPlanPaySerieDto(accountingPlanValue);
        actionsToSave$.push(this.accountingService.saveAccountingPlan(accountingPlanPaySerieDTO));
      } else {
        const retrievedAccountingPlan = this.initialAccountingPlans.find(accountingPlan => accountingPlan.id === accountingPlanValue.id);
        if (retrievedAccountingPlan && !isAccountingPlanEqual(accountingPlanValue, retrievedAccountingPlan)) {
          const accountingPlanPaySerieDTO = createAccountingPlanPaySerieDto(accountingPlanValue);
          actionsToSave$.push(this.accountingService.saveAccountingPlan(accountingPlanPaySerieDTO));
        }
      }

    });

    if (actionsToSave$.length > 0) {
      this.saveInProgress = true;
      this.createAccountingPlanForm();
      forkJoin(actionsToSave$)
        .pipe(
          tap(() => this.populateAccountingPlanAndPaySeries()),
          finalize(() => {
            this.saveInProgress = false;
            if (this.removeClickOutsideListener) {
              this.removeClickOutsideListener();
            }
          }),
          catchError(() => EMPTY)
        )
        .subscribe();
    }
  }

  cliRegIdsToString() {
    return this.cliRegIds.join(',');
  }
}
